import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { useFragment } from "react-relay/hooks";

import { ClubStackNavigatorParamList } from "../../../navigators/ClubNavigator";
import {
  isBookingMultipleTeeTimesAtom,
  teeSheetGroupSizeFilterAtom,
  teeSheetHoleFilterAtom,
  teeSheetListFilterAtom,
} from "../atoms";
import { teeSheetSelectedCourseIdsByFacility } from "../atoms/TeeSheetSelectedCourseIdsByFacility";
import { useResetTeeSheetFilters_club$key } from "./__generated__/useResetTeeSheetFilters_club.graphql";

export const useResetTeeSheetFilters = (
  fragRef: useResetTeeSheetFilters_club$key | null
) => {
  const { facilitySlug } =
    useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>().params;
  const data = useFragment(useResetTeeSheetFiltersFragmentString, fragRef);

  const currentFacility = useMemo(
    () =>
      data?.facilities?.edges?.find((e) => e?.node?.slug === facilitySlug)
        ?.node,
    [data?.facilities?.edges, facilitySlug]
  );

  const setTeeSheetListFilter = useSetAtom(teeSheetListFilterAtom);
  const setTeeSheetGroupSizeFilter = useSetAtom(teeSheetGroupSizeFilterAtom);
  const setTeeSheetHoleFilter = useSetAtom(teeSheetHoleFilterAtom);
  const setBookingMultipleTeeTimes = useSetAtom(isBookingMultipleTeeTimesAtom);
  const _setSelectedCourseIds = useSetAtom(
    teeSheetSelectedCourseIdsByFacility(currentFacility?.id ?? "")
  );

  const resetTeeSheetFilters = useCallback(() => {
    const allCourseIds =
      currentFacility?.courses?.edges?.map((c) => c?.node?.id ?? "") || [];

    _setSelectedCourseIds(allCourseIds);

    setTeeSheetListFilter("All");
    setTeeSheetGroupSizeFilter(0);
    setTeeSheetHoleFilter(null);
    setBookingMultipleTeeTimes(false);
  }, [
    _setSelectedCourseIds,
    currentFacility?.courses?.edges,
    setBookingMultipleTeeTimes,
    setTeeSheetGroupSizeFilter,
    setTeeSheetHoleFilter,
    setTeeSheetListFilter,
  ]);

  return resetTeeSheetFilters;
};

export const useResetTeeSheetFiltersFragmentString = graphql`
  fragment useResetTeeSheetFilters_club on Club {
    id
    facilities {
      edges {
        node {
          id
          slug
          courses {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;
